import Box from "@mui/material/Box";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import CardActions from "@mui/material/CardActions";
import Button from "@mui/material/Button";
import _ from "lodash";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import {
  actionMapZoomFeaturesUpdated,
  actionMapUpdateActiveFeature,
} from "../Map";
import { actionAppBarFloorUpdated, selectorAppBarFloor } from "../AppBar";
import { floorSorter } from "../AppBar/Floor";

const root_ = (theme) => ({
  maxHeight: 115,
  padding: "8px 0",
  borderTop: "1px solid rgba(0,0,0,0.1)",
  overflowY: "scroll",
  "&::-webkit-scrollbar": {
    webkitAppearance: "none",
    width: 7,
    backgroundColor: "rgba(0, 0, 0, 0.1)",
  },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: "2px",
    backgroundColor: "rgba(0, 0, 0, .5)",
    webkitBoxShadow: "0 0 1px rgba(255, 255, 255, .5)",
  },
});

const gridContent_ = (theme) => ({
  alignSelf: "center",
  "& .MuiTypography-overline": {
    lineHeight: "24px",
    height: 24,
    fontSize: "0.7rem",
  },
  "& .MuiButton-root": {
    fontSize: "0.7rem",
  },
});

const accordionBtn_ = (theme) => ({
  padding: "0 4px",
  minWidth: 24,
  minHeight: 24,
  "&:hover": {
    textDecoration: "underline",
  },
});

const cardActionRoot_ = (theme) => ({
  padding: "0px",
});

const row_ = (theme) => ({
  lineHeight: 1,
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    color: "black",
  },
});

export default function BuildingFloorsDisplay({
  id,
  rooms,
  sortBy,
  sortDirection,
}) {
  const dispatch = useDispatch();

  const rows = toRows(id, rooms);
  const buildings = sortRows(rows, sortBy, sortDirection);
  const [firsBuilding] = buildings;
  const firstFloor =
    firsBuilding &&
    firsBuilding.floors &&
    firsBuilding.floors.length &&
    firsBuilding.floors.indexOf("01") === -1
      ? firsBuilding.floors[0]
      : "01";

  const selectFloor = (floor, rrns, bldrecnbr) => {
    dispatch(actionMapZoomFeaturesUpdated({ type: "rrn", data: rrns }));
    dispatch(actionAppBarFloorUpdated(floor));
    dispatch(actionMapUpdateActiveFeature({ bldrecnbr, floor }));
  };

  let selected = null;
  const currentlySelectedFloor = useSelector(selectorAppBarFloor);
  if (currentlySelectedFloor) {
    selected = `${firsBuilding.id}-${currentlySelectedFloor}`;
  } else if (firstFloor) {
    selected = `${firsBuilding.id}-${firstFloor}`;
  }

  const selectHandler = (id, floor, rooms) => {
    const rrns = toRrns(rooms, floor);
    const bldrecnbr = toBldrecnbr(rooms);
    selected = id;
    selectFloor(floor, rrns, bldrecnbr);
  };

  useEffect(() => {
    // This effect only runs the very first time this component
    // opens. If we have building info, automatically switch to a
    // suitable floor IF the user hasn't already selected a floor
    // in the FLOOR dropdown.
    if (!currentlySelectedFloor && firstFloor) {
      const rrns = toRrns(firsBuilding.rooms, firstFloor);
      const bldrecnbr = toBldrecnbr(firsBuilding.rooms);
      setTimeout(() => {
        selectFloor(firstFloor, rrns, bldrecnbr);
      }, 1000);
    }
  }, []);

  return (
    <Box sx={root_}>
      {buildings.map((building) => {
        return (
          <Building
            data={building}
            key={building.id}
            selected={selected}
            selectHandler={selectHandler}
          />
        );
      })}
    </Box>
  );
}

function Building({ data, selected, selectHandler }) {
  const handleFloorClick = (floor, id) => () => {
    selectHandler(`${id}-${floor}`, floor, data.rooms);
  };

  return (
    <CardActions disableSpacing sx={cardActionRoot_}>
      <Grid container align="center" justifyContent="center" sx={row_}>
        <Grid size={{ xs: 4 }} sx={gridContent_}>
          <Typography variant="overline">{data.building}</Typography>
        </Grid>
        <Grid size={{ xs: 8 }} sx={gridContent_}>
          <Floors
            building={data.building}
            floors={data.floors}
            id={data.id}
            handleFloorClick={handleFloorClick}
            selected={selected}
          />
        </Grid>
      </Grid>
    </CardActions>
  );
}

function Floors({ building, id, floors, handleFloorClick, selected }) {
  return (
    <>
      {floors
        .sort(floorSorter)
        .reverse()
        .map((floor) => {
          const isSelected = `${id}-${floor}` === selected;
          return (
            <Tooltip title={`Go To ${building} FLOOR ${floor}`} key={floor}>
              <Button
                variant={!isSelected ? "text" : "outlined"}
                size="small"
                color="primary"
                sx={accordionBtn_}
                onClick={handleFloorClick(floor, id)}
                key={floor}
              >
                {floor}
              </Button>
            </Tooltip>
          );
        })}
    </>
  );
}

// output [{id, buildingName, floors, rooms}, ...]
function toRows(groupId, rooms) {
  const byBuildings = _.groupBy(rooms, "bld_descrshort");
  let data = [];
  for (let building in byBuildings) {
    const id = groupId + "-" + building;
    const brn = byBuildings[building].length
      ? byBuildings[building][0]["bldrecnbr"]
      : "";
    const rooms = _.sortBy(byBuildings[building], ["floor", "rmnbr"]);
    const floors = _.chain(rooms).map("floor").uniq().value().sort();
    data.push({ id, building, brn, rooms, floors });
  }
  return _.sortBy(data, "building");
}

function sortRows(rows, sortBy, sortDirection) {
  if (sortBy === "building" && sortDirection === "ascending") {
    return _.sortBy(rows, "building");
  } else if (sortBy === "building" && sortDirection === "descending") {
    return _.sortBy(rows, "building").reverse();
  }

  return rows;
}

function toRrns(rooms, floor) {
  return rooms
    .filter((room) => room.floor === floor)
    .map((room) => room.rmrecnbr);
}

function toBldrecnbr(rooms) {
  return rooms && rooms.length && rooms[0].bldrecnbr;
}
